import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChefHat, Loader2, CheckCircle2 } from "lucide-react";

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        await login(data.token, data.user);
        toast.success(`Vítejte, ${data.user.name}`);
        nav("/");
      } else {
        await api.post("/auth/register", form);
        setRegistered(true);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Chyba");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:flex w-1/2 bg-primary relative overflow-hidden flex-col justify-between p-14">
        <div className="flex items-center gap-3 relative z-10">
          <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
            <ChefHat className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-white text-lg leading-tight">Kalkulace</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/70">Zdravé stravování</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-5xl font-medium text-white leading-tight">Profesionální kalkulace nákladů výrobků.</h1>
          <p className="text-white/70 mt-5 text-lg max-w-md">Receptury, ceny surovin, kalkulace na míru odběratelům — vše na jednom místě.</p>
        </div>
        <div className="text-white/50 text-sm relative z-10">© {new Date().getFullYear()} Zdravé stravování s.r.o.</div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute right-32 top-20 w-64 h-64 rounded-full bg-white/5" />
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg">Kalkulace</span>
          </div>

          {registered ? (
            <div className="text-center space-y-4" data-testid="register-success">
              <CheckCircle2 className="h-14 w-14 text-primary mx-auto" />
              <h2 className="font-display text-2xl font-medium">Registrace odeslána</h2>
              <p className="text-sm text-muted-foreground">
                Váš účet čeká na schválení administrátorem. Po schválení se budete moci přihlásit.
              </p>
              <Button variant="outline" className="w-full" onClick={() => { setRegistered(false); setMode("login"); }}>
                Zpět na přihlášení
              </Button>
            </div>
          ) : (
            <>
              <h2 className="font-display text-3xl font-medium">{mode === "login" ? "Přihlášení" : "Registrace"}</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">
                {mode === "login" ? "Přihlaste se do svého účtu" : "Vytvořte si účet (vyžaduje schválení)"}
              </p>

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="name">Jméno</Label>
                    <Input id="name" data-testid="auth-name" className="mt-1.5" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Vaše jméno" />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">E-mail</Label>
                  <Input id="email" type="email" required data-testid="auth-email" className="mt-1.5" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vas@email.cz" />
                </div>
                <div>
                  <Label htmlFor="password">Heslo</Label>
                  <Input id="password" type="password" required data-testid="auth-password" className="mt-1.5" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
                </div>
                {error && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}
                <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="auth-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {mode === "login" ? "Přihlásit se" : "Registrovat"}
                </Button>
              </form>

              <div className="text-center mt-6 text-sm text-muted-foreground">
                {mode === "login" ? "Nemáte účet? " : "Už máte účet? "}
                <button data-testid="auth-toggle" className="text-primary font-medium hover:underline"
                  onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
                  {mode === "login" ? "Zaregistrujte se" : "Přihlaste se"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
